/**
 * employee.ts — 직원 명부 마스터 (급여관리용).
 *
 * Firestore: `employees/{이름}` (이름을 doc id = 자연키로 사용)
 *   → 엑셀 명부 업로드 시 이름 기준 멱등 upsert. 18명 규모라 이름 충돌 없음.
 *   → 향후 동명이인 발생 시 doc id 에 접미사(_2 등)로 분기.
 *
 * 겸직: 한 사람이 두 법인 소속일 수 있음(정의관·김영미 = 경원+중앙).
 *   → 사람은 1 레코드, `companies` 배열로 소속을 담는다. 급여명세서는 회사별 각 1건 매칭.
 *
 * 소속 값은 짧은 형태(경원/중앙)로 보관. 급여명세서 PDF·표시에는 경원산업/중앙산업으로 확장.
 *
 * ⚠️ 알림톡/SMS 발송은 최종 목표지만 1단계 명부에는 수신처 컬럼을 두지 않는다(사장님 지시).
 *   생년월일은 추후 발송 포털의 본인확인(2차 열쇠)에 재사용하므로 여기서 보존.
 */

export const PAYROLL_COMPANIES = ["경원", "중앙"] as const;
export type PayrollCompany = (typeof PAYROLL_COMPANIES)[number];

/** 법인 공간 키 → 급여 소속 값 (법인 공간의 급여관리는 그 법인 소속만 보여 준다, D-27) */
export const TENANT_PAYROLL_COMPANY: Record<"joongang" | "gyeongwon", PayrollCompany> = {
  joongang: "중앙",
  gyeongwon: "경원",
};

/** 짧은 소속 → 명세서/문서용 정식 법인명 */
export const COMPANY_FULL_NAME: Record<PayrollCompany, string> = {
  경원: "경원산업",
  중앙: "중앙산업",
};

export interface Employee {
  /** Firestore doc id = 이름 (자연키) */
  id: string;
  name: string;
  /** 급여 법인(겸직 시 복수) — 근로계약·급여·연차·4대보험 기준. 근무 법인(tenants/{t}/members)과 다를 수 있다 */
  companies: PayrollCompany[];
  /** 생년월일 YYYY-MM-DD (발송 포털 본인확인용). 2001년생 이후 없어 6자리는 19XX로 확장 */
  birth: string;
  /** 연락처 (선택) */
  phone?: string;
  /** 이메일 (선택) */
  email?: string;
  active: boolean;
  edited_at?: string;
  edited_by?: string;
  /**
   * 연결된 로그인 계정 uid(통합관리 › 사용자 상세 [명부 연결], 전체 admin). 없으면 null.
   * 한 uid 는 명부 1개. 급여 계산·명세서는 이 값을 쓰지 않는다(엑셀 명부 upsert 는 merge 라 보존됨).
   */
  uid: string | null;
}

/** 소속 문자열을 정규화된 PayrollCompany 로 변환. 못 알아보면 null. */
export function normalizeCompany(raw: unknown): PayrollCompany | null {
  if (typeof raw !== "string") return null;
  const s = raw.trim();
  if (!s) return null;
  if (s.startsWith("경원")) return "경원";
  if (s.startsWith("중앙")) return "중앙";
  return null;
}

/** 여러 소속 후보(소속1/소속2 등)를 중복 제거된 PayrollCompany 배열로. */
export function toCompanies(...raws: unknown[]): PayrollCompany[] {
  const out: PayrollCompany[] = [];
  for (const r of raws) {
    const c = normalizeCompany(r);
    if (c && !out.includes(c)) out.push(c);
  }
  return out;
}

/**
 * 생년월일을 `YYYY-MM-DD` 로 정규화. 6자리(YYMMDD)·8자리(YYYYMMDD)·구분자(1979.01.19)·
 * 이미 YYYY-MM-DD 형태 모두 흡수. 2001년생 이후가 없어 6자리는 항상 19XX 로 확장.
 * 유효한 날짜가 아니면 "".
 */
export function normalizeBirthYmd(raw: unknown): string {
  const digits = String(raw ?? "").replace(/[^0-9]/g, "");
  let y: number, mo: number, d: number;
  if (digits.length === 8) {
    y = +digits.slice(0, 4); mo = +digits.slice(4, 6); d = +digits.slice(6, 8);
  } else if (digits.length === 6) {
    y = 1900 + +digits.slice(0, 2); mo = +digits.slice(2, 4); d = +digits.slice(4, 6);
  } else {
    return "";
  }
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || y < 1900 || y > 2100) return "";
  return `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** YYYY-MM-DD → YYMMDD (열람 게이트 6자리 입력 비교용). 형식 아니면 "". */
export function birth6(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  return m ? m[1].slice(2) + m[2] + m[3] : "";
}

/** YYYY-MM-DD → YYYYMMDD (8자리 입력 비교용). 형식 아니면 "". */
export function birth8(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  return m ? m[1] + m[2] + m[3] : "";
}

/** 입력 중 자동 하이픈 — 숫자를 YYYY-MM-DD 로 점진 포맷(8자리 기준). 6자리는 blur 시 normalize. */
export function formatBirthInput(raw: string): string {
  const d = raw.replace(/\D/g, "").slice(0, 8);
  if (d.length <= 4) return d;
  if (d.length <= 6) return `${d.slice(0, 4)}-${d.slice(4)}`;
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6)}`;
}

/** 입력 중 자동 하이픈 — 한국 전화번호(휴대폰·지역번호). */
export function formatPhoneKR(raw: string): string {
  const d = raw.replace(/\D/g, "").slice(0, 11);
  if (d.length < 4) return d;
  if (d.startsWith("02")) {
    if (d.length < 6) return `${d.slice(0, 2)}-${d.slice(2)}`;
    if (d.length < 10) return `${d.slice(0, 2)}-${d.slice(2, d.length - 4)}-${d.slice(d.length - 4)}`;
    return `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6, 10)}`;
  }
  if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`;
  if (d.length < 11) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
}

function coerceDate(v: unknown): string | undefined {
  if (typeof v === "string") return v;
  if (v && typeof (v as { toDate?: () => Date }).toDate === "function") {
    return (v as { toDate: () => Date }).toDate().toISOString();
  }
  return undefined;
}

export function coerceEmployee(id: string, data: Record<string, unknown>): Employee {
  const rawCompanies = Array.isArray(data.companies) ? data.companies : [];
  const companies = toCompanies(...rawCompanies);
  return {
    id,
    name: typeof data.name === "string" ? data.name : id,
    companies,
    birth: normalizeBirthYmd(data.birth), // 레거시 6자리 저장분도 읽을 때 19XX-XX-XX 로 확장
    phone: typeof data.phone === "string" && data.phone ? data.phone : undefined,
    email: typeof data.email === "string" && data.email ? data.email : undefined,
    active: data.active !== false,
    edited_at: coerceDate(data.edited_at),
    edited_by: typeof data.edited_by === "string" ? data.edited_by : undefined,
    uid: typeof data.uid === "string" && data.uid ? data.uid : null,
  };
}

export function companiesLabel(companies: PayrollCompany[]): string {
  if (companies.length === 0) return "—";
  return companies.join("·");
}
